import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  BarChart3, TrendingUp, Cpu, DollarSign, Briefcase, Users,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [analyticsData, usageData] = await Promise.all([
          api.getAdminAnalytics(days),
          api.getAdminUsage(),
        ]);
        setData(analyticsData);
        setUsage(usageData);
      } catch (err) {
        console.error('Analytics load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data || !usage) {
    return <div className="text-muted-foreground text-center py-8">No analytics data available.</div>;
  }

  return (
    <div data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Analytics
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Usage, Revenue & Performance</p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <Button
              key={d}
              variant={days === d ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(d)}
              data-testid={`period-${d}d`}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI icon={Cpu} label="Total API Calls" value={usage.total_calls.toLocaleString()} />
        <KPI icon={BarChart3} label="Total Tokens" value={formatNumber(usage.total_tokens)} />
        <KPI icon={DollarSign} label="Revenue" value={`${usage.total_customer_cost.toFixed(2)} Credits`} accent />
        <KPI icon={TrendingUp} label="Profit" value={`${usage.total_profit.toFixed(2)} Credits`} accent />
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Daily Usage Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Daily Token Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.daily_usage.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No usage data in this period.</p>
            ) : (
              <MiniBarChart data={data.daily_usage} valueKey="tokens" labelKey="date" color="var(--primary)" />
            )}
          </CardContent>
        </Card>

        {/* Daily Revenue Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Daily Revenue (Credits)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.daily_revenue.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No revenue data in this period.</p>
            ) : (
              <MiniBarChart data={data.daily_revenue} valueKey="revenue" labelKey="date" color="#10b981" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Provider Breakdown & Job Stats */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Provider Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Provider Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {usage.by_provider.length === 0 ? (
                <p className="text-sm text-muted-foreground">No provider data yet.</p>
              ) : (
                usage.by_provider.map(p => (
                  <div key={p.provider} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30" data-testid={`provider-${p.provider}`}>
                    <div>
                      <span className="font-medium text-sm capitalize">{p.provider}</span>
                      <span className="text-xs text-muted-foreground ml-2">{p.calls} calls</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">{formatNumber(typeof p.tokens === 'number' ? p.tokens : p.tokens?.total || 0)} tok</span>
                      <span className="text-emerald-400 font-mono">{p.customer_cost.toFixed(4)}</span>
                      <ProfitBadge value={p.profit} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Job Status Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Job Status ({days}d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(data.job_stats).length === 0 ? (
              <p className="text-sm text-muted-foreground">No jobs in this period.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(data.job_stats).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30" data-testid={`job-stat-${status}`}>
                    <span className={`text-sm font-medium capitalize ${statusColor(status)}`}>{status}</span>
                    <span className="text-lg font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Jobs & Org Revenue */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Jobs by Cost */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <Briefcase className="w-4 h-4" /> Top Jobs by Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_jobs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No jobs with costs yet.</p>
            ) : (
              <div className="space-y-2">
                {data.top_jobs.map(j => (
                  <div key={j.job_id} className="flex items-center justify-between p-2 rounded border border-border text-sm" data-testid={`top-job-${j.job_id}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="font-mono text-xs truncate">{j.ip}</span>
                      <span className="text-muted-foreground truncate">{j.blacklist}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs shrink-0">
                      <span className="text-muted-foreground">{formatNumber(j.tokens)} tok</span>
                      <span className="font-mono text-amber-400">{j.cost.toFixed(4)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Org Revenue */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" /> Organization Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.org_revenue.length === 0 ? (
              <p className="text-sm text-muted-foreground">No organization data yet.</p>
            ) : (
              <div className="space-y-2">
                {data.org_revenue.map(o => (
                  <div key={o.org_id} className="flex items-center justify-between p-2 rounded border border-border text-sm" data-testid={`org-${o.org_id}`}>
                    <span className="font-medium truncate">{o.org_name}</span>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-emerald-400">+{o.total_topups.toFixed(2)}</span>
                      <span className="text-red-400">-{o.total_usage.toFixed(4)}</span>
                      <span className="font-mono text-muted-foreground">Bal: {o.current_balance.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// --- Helper Components ---

function KPI({ icon: Icon, label, value, accent }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`w-4 h-4 ${accent ? 'text-emerald-400' : 'text-primary'}`} />
          <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">{label}</span>
        </div>
        <div className={`text-2xl font-semibold ${accent ? 'text-emerald-400' : ''}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function ProfitBadge({ value }) {
  if (value > 0) return <span className="text-emerald-400 flex items-center gap-0.5"><ArrowUpRight className="w-3 h-3" />{value.toFixed(4)}</span>;
  if (value < 0) return <span className="text-red-400 flex items-center gap-0.5"><ArrowDownRight className="w-3 h-3" />{value.toFixed(4)}</span>;
  return <span className="text-muted-foreground flex items-center gap-0.5"><Minus className="w-3 h-3" />0</span>;
}

function MiniBarChart({ data, valueKey, labelKey, color }) {
  const maxVal = Math.max(...data.map(d => d[valueKey]), 1);
  const barCount = data.length;
  const showLabels = barCount <= 14;

  return (
    <div className="flex items-end gap-[2px] h-32" data-testid="mini-bar-chart">
      {data.map((d, i) => {
        const pct = (d[valueKey] / maxVal) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
            {/* Tooltip */}
            <div className="absolute bottom-full mb-1 hidden group-hover:block bg-popover text-popover-foreground text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-10 border border-border">
              {d[labelKey]}: {typeof d[valueKey] === 'number' ? d[valueKey].toLocaleString() : d[valueKey]}
            </div>
            <div
              className="w-full rounded-t transition-all"
              style={{
                height: `${Math.max(pct, 2)}%`,
                backgroundColor: color,
                opacity: 0.8,
                minHeight: '2px',
              }}
            />
            {showLabels && (
              <span className="text-[8px] text-muted-foreground mt-0.5 truncate w-full text-center">
                {d[labelKey]?.slice(5)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatNumber(n) {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n?.toLocaleString() || '0';
}

function statusColor(status) {
  const map = { success: 'text-emerald-400', failed: 'text-red-400', in_progress: 'text-amber-400', researching: 'text-primary', cancelled: 'text-muted-foreground' };
  return map[status] || 'text-foreground';
}
